import { bool } from "prop-types";
import { forwardRef } from "react";

import styled from "styled-components/macro";

import FnrTypography from "FnrTypography";
import {
  useIsHouseholdGroupingEnabled,
  useIsSingleUserHousehold,
} from "HouseholdProfile/API/queryHooks";

import FnrBadge from "UI/FnrBadge";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrNetworkPhoto from "UI/FnrNetworkPhoto";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import truncate from "Utilities/truncate";

import { useUserChipContext } from "../UserChip";
import { useAge } from "../stateHooks";

const SDiv = styled("div")`
  margin-top: -2px;
`;

const SFnrHorizontalGap = styled(FnrHorizontalGap)`
  padding: 4px;
  min-width: 140px;
  max-width: 100%;

  @media screen and (max-width: 680px) {
    min-width: 0;
  }
`;

const SFnrVerticalGap = styled(FnrVerticalGap)`
  line-height: 1rem;
`;

const maxAgeToShow = 26;

const PrimaryContents = forwardRef(({ isSelected }, ref) => {
  const isHouseholdGroupingEnabled = useIsHouseholdGroupingEnabled();
  const isSingleUserHousehold = useIsSingleUserHousehold();
  const age = useAge();

  const { isHOH, photoUrl, firstName, isChild, isArchived, isMobile } =
    useUserChipContext();

  const showHoHIcon = Boolean(
    isHOH && isHouseholdGroupingEnabled && !isSingleUserHousehold
  );

  const hasFirstName =
    truncate(firstName, { length: 9, breakWord: true })?.length > 0;
  const fName = hasFirstName
    ? truncate(firstName, { length: 9, breakWord: true })
    : isChild
    ? "Child"
    : "Adult";

  return (
    <SFnrHorizontalGap gap={4} ref={ref} fullWidth>
      <FnrHorizontalGap
        gap={isMobile ? 8 : 13}
        style={{ opacity: isArchived ? "0.4" : 1 }}
      >
        <FnrNetworkPhoto size="lg" src={photoUrl} shape="square" thick={0} />
        <SFnrVerticalGap gap={2}>
          <FnrTypography
            data-cy="HouseholdProfile_UserChip_PrimaryContents_Name"
            fontSize={isHOH ? "lg" : "md"}
            type="copy"
            lineHeight={0}
          >
            {fName}
          </FnrTypography>
          {age < maxAgeToShow && age >= 0 && isChild && (
            <SDiv>
              <FnrBadge borderRadius={4} color="taupe" paddingY={0}>
                AGE {age}
              </FnrBadge>
            </SDiv>
          )}
        </SFnrVerticalGap>
      </FnrHorizontalGap>
      {showHoHIcon ? (
        <FnrContainedIcon
          color={isSelected ? "yellow" : "faintTaupe"}
          icon="crown fas"
        />
      ) : null}
    </SFnrHorizontalGap>
  );
});

PrimaryContents.propTypes = {
  isSelected: bool,
};

export default PrimaryContents;
